import React from 'react';
import { Card, Col, Descriptions, Empty, Row, Typography } from 'antd';

const { Text } = Typography;

export type AlticeSaleRecord = Record<string, unknown>;

export interface AlticeSaleDetailViewProps {
  sale: AlticeSaleRecord;
  /** Show creator, lead name, timestamps (team validation context). */
  showSystemInfo?: boolean;
  creatorUsername?: string;
  leadName?: string;
  /** Hide section cards that have no filled fields. Default true. */
  hideEmptySections?: boolean;
  maxHeight?: number | string;
}

type FieldDef = { label: string; key: string };

type SectionDef = {
  title: string;
  fields: FieldDef[];
};

const DATE_KEYS = new Set([
  'data_nascimento',
  'data_preferencial_agendamento',
  'created_at',
  'updated_at',
]);

const ALTICE_SALE_SECTIONS: SectionDef[] = [
  {
    title: 'Identificação do Cliente',
    fields: [
      { label: 'Tipo Cliente', key: 'tipo_cliente' },
      { label: 'Número Serviço Atual', key: 'numero_servico_atual' },
      { label: 'Segmento', key: 'segmento' },
      { label: 'Categoria', key: 'categoria' },
      { label: 'Tecnologia', key: 'tecnologia' },
      { label: 'Tipologia', key: 'tipologia' },
      { label: 'Pacote ID', key: 'pacote_id' },
      { label: 'Nome', key: 'nome' },
      { label: 'Contribuinte', key: 'contribuinte' },
      { label: 'CC/BI/Passaporte', key: 'cc_bi_passaporte' },
      { label: 'Data Nascimento', key: 'data_nascimento' },
      { label: 'Nacionalidade', key: 'nacionalidade' },
      { label: 'Morada Instalação', key: 'morada_instalacao' },
      { label: 'Coordenadas', key: 'coordenadas' },
      { label: 'Código Postal', key: 'cod_postal_instalacao' },
      { label: 'Localidade', key: 'localidade_instalacao' },
      { label: 'Telefone Contacto', key: 'telefone_contacto' },
      { label: 'Email', key: 'email' },
    ],
  },
  {
    title: 'Administrador da Conta',
    fields: [
      { label: 'Nome', key: 'admin_conta_nome' },
      { label: 'Telefone', key: 'admin_conta_telefone' },
      { label: 'Telemóvel', key: 'admin_conta_telemovel' },
      { label: 'Email', key: 'admin_conta_email' },
      { label: 'NIF', key: 'admin_conta_nif' },
      { label: 'BI', key: 'admin_conta_bi' },
    ],
  },
  {
    title: 'Faturação',
    fields: [
      { label: 'Débito Direto', key: 'debito_direto' },
      { label: 'Fatura Eletrónica', key: 'fatura_eletronica' },
      { label: 'Telemóvel Aviso Faturação', key: 'telemovel_aviso_faturacao' },
      { label: 'IBAN', key: 'IBAN' },
      { label: 'Nome Titular Conta Bancária', key: 'nome_titular_conta_bancaria' },
    ],
  },
  {
    title: 'Serviços',
    fields: [
      { label: 'Televisão', key: 'televisao' },
      { label: 'Televisão — Nº Canais', key: 'televisao_num_canais' },
      { label: 'Primeira MEO Box', key: 'primeira_meo_box' },
      { label: 'Internet Fixa', key: 'internet_fixa' },
      { label: 'Internet Fixa — Velocidade Download', key: 'internet_fixa_velocidade_download' },
      { label: 'Internet Fixa — Velocidade Upload', key: 'internet_fixa_velocidade_upload' },
      { label: 'Internet Fixa — Tráfego Ilimitado', key: 'internet_fixa_trafego_ilimitado' },
      { label: 'Internet Fixa — Tráfego Limitado', key: 'internet_fixa_trafego_limitado_a' },
      { label: 'Telefone Fixo', key: 'telefone_fixo' },
      { label: 'Telefone Fixo — Tarifário', key: 'telefone_fixo_tarifario' },
      { label: 'Telefone Fixo — Equipamento', key: 'telefone_fixo_equipamento' },
      { label: 'Portabilidade Fixa', key: 'portabilidade_fixa' },
      { label: 'Nº Telefone Fixo', key: 'num_telefone_fixo' },
      { label: 'Operadora Fixo', key: 'operadora_fixo' },
      { label: 'CVP Fixo', key: 'CVP_fixo' },
      { label: 'Telefone Móvel', key: 'telefone_movel' },
      { label: 'Telefone Móvel — Nº Cartões', key: 'telefone_movel_num_cartoes' },
      { label: 'Telefone Móvel — Nº Minutos', key: 'telefone_movel_num_minutos' },
      { label: 'Telefone Móvel — Nº SMS', key: 'telefone_movel_num_sms' },
      { label: 'Telefone Móvel — Nº GB', key: 'telefone_movel_num_gb' },
      { label: 'Internet Móvel', key: 'internet_movel' },
      { label: 'Internet Móvel — Plafond', key: 'internet_movel_plafond' },
      { label: 'Internet Móvel — Router', key: 'internet_movel_router' },
      { label: 'Internet Móvel — Router IMEI', key: 'internet_movel_router_imei' },
    ],
  },
  {
    title: 'Detalhes do Pacote',
    fields: [
      { label: 'Período Fidelização', key: 'periodo_fidelizacao' },
      { label: 'Preço', key: 'preco' },
    ],
  },
  {
    title: 'Serviços Adicionais',
    fields: [
      { label: 'Nº Box', key: 'servicos_adicionais_num_box' },
      { label: 'Tipo Box', key: 'servicos_adicionais_tipo_box' },
      { label: 'Box Valor', key: 'servicos_adicionais_box_valor' },
      { label: 'Cartões Telefone Móvel', key: 'servicos_adicionais_cartoes_telefone_movel' },
      { label: 'Cartões Telefone Móvel Valor', key: 'servicos_adicionais_cartoes_telefone_movel_valor' },
      { label: 'GB Internet Móvel', key: 'servicos_adicionais_gb_internet_movel' },
      { label: 'GB Internet Móvel Valor', key: 'servicos_adicionais_gb_internet_movel_valor' },
      { label: 'Internet Móvel PC/Tablet', key: 'servicos_adicionais_internet_movel_pc_tablet' },
      { label: 'Internet Móvel PC/Tablet Valor', key: 'servicos_adicionais_internet_movel_pc_tablet_valor' },
      { label: 'Equipamento Telefone Fixo', key: 'servicos_adicionais_equipamento_telefone_fixo' },
      { label: 'Aquisição Equipamento Telefone Fixo', key: 'servicos_adicionais_aquisicao_equipamento_telefone_fixo' },
      { label: 'Equipamento Telefone Fixo Modalidade', key: 'servicos_adicionais_equipamento_telefone_fixo_modalidade' },
      { label: 'Equipamento Telefone Fixo Valor', key: 'servicos_adicionais_equipamento_telefone_fixo_valor' },
      { label: 'Outros', key: 'servicos_adicionais_outros' },
      { label: 'Outros Valor', key: 'servicos_adicionais_outros_valor' },
      { label: 'Total', key: 'servicos_adicionais_total' },
    ],
  },
  {
    title: 'Equipamentos',
    fields: [
      { label: 'Tipo Instalação', key: 'equipamentos_tipo_instalacao' },
      { label: 'MEOBox Grav', key: 'equipamentos_meobox_grav' },
      { label: 'MEOBox Sem Grav', key: 'equipamentos_meobox_sem_grav' },
      { label: 'Box 4K', key: 'equipamentos_box_4k' },
      { label: 'Regime Equipamentos', key: 'equipamentos_regime_equipamentos' },
      { label: 'Quant. Caixas TDT', key: 'equipamentos_quant_caixas_tdt' },
      { label: 'Serial Number', key: 'equipamentos_serial_number' },
      { label: 'Card', key: 'equipamentos_card' },
      { label: 'Serial Number 2', key: 'equipamentos_serial_number2' },
      { label: 'Card 2', key: 'equipamentos_card2' },
      { label: 'Serial Number 3', key: 'equipamentos_serial_number3' },
      { label: 'Card 3', key: 'equipamentos_card3' },
    ],
  },
  {
    title: 'Ofertas e Descontos',
    fields: [
      { label: 'Desconto Instalação/Ativação', key: 'oferta_desconto_instalacao_ativacao' },
      { label: 'Serviço MEO Go', key: 'oferta_servico_meo_go' },
      { label: 'Desconto Média Fidelização', key: 'desconto_incluido_media_fidelizacao' },
      { label: 'Outras Ofertas', key: 'outras_ofertas' },
    ],
  },
  ...([1, 2, 3, 4, 5] as const).map((n) => ({
    title: `Telefone Móvel ${n}`,
    fields: [
      { label: 'Número', key: `telefone_movel${n}_numero` },
      { label: 'Modalidade', key: `telefone_movel${n}_modalidade` },
      { label: 'Mesmo Titular', key: `telefone_movel${n}_mesmo_titular` },
      { label: 'Nome Utilizador', key: `telefone_movel${n}_nome_utilizador` },
      { label: 'Contribuinte', key: `telefone_movel${n}_contribuinte` },
      { label: 'CC/BI/Passaporte', key: `telefone_movel${n}_cc_bi_passaporte` },
      { label: 'Portabilidade', key: `telefone_movel${n}_portabilidade` },
      { label: 'Operadora', key: `telefone_movel${n}_operadora` },
      { label: 'CVP', key: `telefone_movel${n}_CVP` },
    ],
  })),
  {
    title: 'MEO Energy',
    fields: [
      { label: 'Morada Ponto Entrega', key: 'morada_ponto_entrega' },
      { label: 'Código Postal', key: 'morada_ponto_entrega_cp7' },
      { label: 'Localidade', key: 'morada_ponto_entrega_localidade' },
      { label: 'CPE', key: 'CPE' },
      { label: 'Alteração Titular', key: 'alteracao_titular' },
      { label: 'Potência Contratada', key: 'potencia_contratada' },
      { label: 'Ciclo', key: 'ciclo' },
      { label: 'Tarifa', key: 'tarifa' },
      { label: 'Fornecedor Atual', key: 'fornecedor_atual' },
      { label: 'Tarifa Social', key: 'tarifa_social' },
    ],
  },
  {
    title: 'Agendamento e Observações',
    fields: [
      { label: 'Autoriza Tratamento Dados/Comunicações', key: 'autoriza_tratamento_dados_comunicacoes' },
      { label: 'Observações', key: 'observacoes' },
      { label: 'Data Preferencial Agendamento', key: 'data_preferencial_agendamento' },
      { label: 'Horário Preferencial Agendamento', key: 'horario_preferencial_agendamento' },
    ],
  },
];

function hasValue(value: unknown): boolean {
  return value !== null && value !== undefined && String(value).trim() !== '';
}

function formatValue(key: string, value: unknown): string {
  if (value === null || value === undefined) return '';
  if (DATE_KEYS.has(key)) {
    const text = String(value).trim();
    if (!text) return '';
    const parsed = new Date(text);
    if (!Number.isNaN(parsed.getTime())) {
      if (key === 'created_at' || key === 'updated_at') {
        return parsed.toLocaleString('pt-PT');
      }
      return parsed.toLocaleDateString('pt-PT');
    }
  }
  return String(value);
}

function SectionCard({
  title,
  fields,
  sale,
  hideEmptySections,
}: {
  title: string;
  fields: FieldDef[];
  sale: AlticeSaleRecord;
  hideEmptySections: boolean;
}) {
  const items = fields
    .map(({ label, key }) => {
      const raw = sale[key];
      if (!hasValue(raw)) return null;
      return (
        <Descriptions.Item key={key} label={label}>
          {formatValue(key, raw)}
        </Descriptions.Item>
      );
    })
    .filter(Boolean);

  if (hideEmptySections && items.length === 0) return null;

  return (
    <Col span={24}>
      <Card title={title} size="small" style={{ marginBottom: 16 }}>
        {items.length > 0 ? (
          <Descriptions column={{ xs: 1, sm: 1, md: 2 }} size="small" bordered>
            {items}
          </Descriptions>
        ) : (
          <Text type="secondary">Sem dados nesta secção.</Text>
        )}
      </Card>
    </Col>
  );
}

const AlticeSaleDetailView: React.FC<AlticeSaleDetailViewProps> = ({
  sale,
  showSystemInfo = false,
  creatorUsername,
  leadName,
  hideEmptySections = true,
  maxHeight = '70vh',
}) => {
  if (!sale) {
    return <Empty description="Formulário não encontrado." />;
  }

  const visibleSections = ALTICE_SALE_SECTIONS.filter((section) => {
    if (!hideEmptySections) return true;
    return section.fields.some(({ key }) => hasValue(sale[key]));
  });

  return (
    <div style={{ maxHeight, overflowY: 'auto', paddingRight: 4 }}>
      <Row gutter={[16, 0]}>
        {visibleSections.map((section) => (
          <SectionCard
            key={section.title}
            title={section.title}
            fields={section.fields}
            sale={sale}
            hideEmptySections={hideEmptySections}
          />
        ))}

        {showSystemInfo ? (
          <Col span={24}>
            <Card title="Informações do Sistema" size="small">
              <Descriptions column={{ xs: 1, sm: 1, md: 2 }} size="small" bordered>
                <Descriptions.Item label="ID">{String(sale.id ?? '—')}</Descriptions.Item>
                <Descriptions.Item label="Lead ID">{String(sale.lead_id ?? '—')}</Descriptions.Item>
                <Descriptions.Item label="Criado por">{creatorUsername || '—'}</Descriptions.Item>
                <Descriptions.Item label="Lead">{leadName || '—'}</Descriptions.Item>
                {hasValue(sale.created_at) ? (
                  <Descriptions.Item label="Submetido em">
                    {formatValue('created_at', sale.created_at)}
                  </Descriptions.Item>
                ) : null}
                {hasValue(sale.updated_at) ? (
                  <Descriptions.Item label="Atualizado em">
                    {formatValue('updated_at', sale.updated_at)}
                  </Descriptions.Item>
                ) : null}
              </Descriptions>
            </Card>
          </Col>
        ) : hasValue(sale.created_at) ? (
          <Col span={24}>
            <Card size="small">
              <Text type="secondary">
                Submetido em {formatValue('created_at', sale.created_at)}
              </Text>
            </Card>
          </Col>
        ) : null}
      </Row>
    </div>
  );
};

export default AlticeSaleDetailView;
